import { api, apiFetch } from "./client";
import type * as T from "./types";

const enc = encodeURIComponent;

/* --- auth ---------------------------------------------------------------- */

export const authApi = {
  signup: (body: {
    email: string;
    password: string;
    fullName: string;
    businessName: string;
    plan: T.PlanId;
    inviteCode?: string;
    termsVersion: string;
    marketingOptIn: boolean;
    platform: "web";
    turnstileToken: string;
  }) => api.post<T.AuthSession>("/v1/auth/signup", body, { auth: false }),

  login: (body: {
    email: string;
    password: string;
    platform?: "web";
    deviceName?: string;
    turnstileToken: string;
  }) => api.post<T.AuthSession>("/v1/auth/login", body, { auth: false }),

  logout: (refreshToken: string) =>
    api.post<void>("/v1/auth/logout", { refreshToken }, { auth: false }),

  me: () => api.get<T.MeResponse>("/v1/auth/me"),

  verifyEmail: (body: { token: string } | { email: string; code: string }) =>
    api.post<T.VerifyEmailResponse>("/v1/auth/verify-email", body, { auth: false }),

  resendVerification: () => api.post<void>("/v1/auth/resend-verification"),

  forgotPassword: (body: { email: string; turnstileToken: string }) =>
    api.post<void>("/v1/auth/forgot-password", body, { auth: false }),

  resetPassword: (body: { token: string; password: string }) =>
    api.post<void>("/v1/auth/reset-password", body, { auth: false }),

  changePassword: (body: { currentPassword: string; newPassword: string }) =>
    api.post<{ reauthRequired: boolean }>("/v1/auth/change-password", body),

  sessions: () => api.get<T.Paginated<T.DeviceSession> | { items: T.DeviceSession[] }>("/v1/auth/sessions"),

  revokeSession: (id: string) => api.del<void>(`/v1/auth/sessions/${enc(id)}`),
};

/* --- passkeys ------------------------------------------------------------ */

export const passkeyApi = {
  registerOptions: () => api.post<{ options: unknown }>("/v1/auth/passkeys/register/options"),
  registerVerify: (body: { response: unknown; name?: string }) =>
    api.post<{ verified: boolean; passkey?: T.Passkey }>("/v1/auth/passkeys/register/verify", body),
  loginOptions: (body: { email?: string }) =>
    api.post<{ options: unknown; challengeId: string }>("/v1/auth/passkeys/login/options", body, {
      auth: false,
    }),
  loginVerify: (body: { challengeId: string; response: unknown; platform?: "web" }) =>
    api.post<T.AuthSession>("/v1/auth/passkeys/login/verify", body, { auth: false }),
  list: () => api.get<{ items: T.Passkey[] }>("/v1/auth/passkeys"),
  rename: (id: string, name: string) => api.patch<T.Passkey>(`/v1/auth/passkeys/${enc(id)}`, { name }),
  remove: (id: string) => api.del<void>(`/v1/auth/passkeys/${enc(id)}`),
};

/* --- reference (public) -------------------------------------------------- */

export const referenceApi = {
  plans: () => api.get<T.PlansResponse>("/v1/reference/plans", { auth: false }),
  sectors: () => api.get<{ items: T.Sector[] }>("/v1/reference/sectors", { auth: false }),
  todoCategories: () => api.get<{ items: T.TodoCategoryRef[] }>("/v1/reference/todo-categories", { auth: false }),
  badges: () => api.get<{ items: T.BadgeRef[] }>("/v1/reference/badges", { auth: false }),
};

/* --- businesses ---------------------------------------------------------- */

export const businessApi = {
  get: (businessId: string) => api.get<T.Business>(`/v1/businesses/${enc(businessId)}`),

  update: (
    businessId: string,
    body: Partial<{
      name: string;
      sectorId: string | null;
      website: string | null;
      description: string | null;
      /** The API's column is employee_count, exposed as employeeCount. */
      employeeCount: number | null;
      /** Theme is two flat fields, not a nested object. */
      themeMode: T.ThemeMode;
      themePrimary: string | null;
    }>,
    // The reply is { updated: true, fields: [...] } — not the business.
  ) => api.patch<{ updated: boolean; fields: string[] }>(`/v1/businesses/${enc(businessId)}`, body),

  create: (body: { name: string; sectorId?: string | null; plan?: T.PlanId }) =>
    api.post<T.Business>("/v1/businesses", body),

  uploadLogo: (businessId: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return api.post<{ logoUrl: string; theme?: T.BusinessTheme }>(
      `/v1/businesses/${enc(businessId)}/logo`,
      form,
    );
  },

  members: (businessId: string) =>
    api.get<{ items: T.Member[] }>(`/v1/businesses/${enc(businessId)}/members`),

  invite: (businessId: string, body: { email: string; role: T.MemberRole }) =>
    api.post<{ invited: boolean; email: string }>(`/v1/businesses/${enc(businessId)}/members/invite`, body),

  updateMemberRole: (businessId: string, userId: string, role: T.MemberRole) =>
    api.patch<T.Member>(`/v1/businesses/${enc(businessId)}/members/${enc(userId)}`, { role }),

  removeMember: (businessId: string, userId: string) =>
    api.del<void>(`/v1/businesses/${enc(businessId)}/members/${enc(userId)}`),

  companiesHouseSearch: (businessId: string, q: string) =>
    api.get<{ items: T.CompaniesHouseResult[] }>(
      `/v1/businesses/${enc(businessId)}/companies-house/search`,
      { query: { q } },
    ),

  companiesHouseLink: (businessId: string, body: { companyNumber: string; overwriteExisting?: boolean }) =>
    api.post<T.CompaniesHouseLinkResponse>(
      `/v1/businesses/${enc(businessId)}/companies-house/link`,
      body,
    ),
};

/* --- billing ------------------------------------------------------------- */

export const billingApi = {
  subscription: (businessId: string) =>
    api.get<T.Subscription>(`/v1/billing/${enc(businessId)}/subscription`),

  checkout: (
    businessId: string,
    body: { plan: T.PlanId; interval: T.BillingInterval; successUrl?: string; cancelUrl?: string },
  ) => api.post<{ checkoutUrl: string }>(`/v1/billing/${enc(businessId)}/checkout`, body),

  portal: (businessId: string, returnUrl?: string) =>
    api.post<{ portalUrl: string }>(`/v1/billing/${enc(businessId)}/portal`, { returnUrl }),

  invoices: (businessId: string) =>
    api.get<{ items: T.Invoice[] }>(`/v1/billing/${enc(businessId)}/invoices`),
};

/* --- metrics ------------------------------------------------------------- */

export const metricsApi = {
  periods: (businessId: string, params: { months?: number; endPeriod?: string } = {}) =>
    api.get<T.MetricsPeriodsResponse>(`/v1/metrics/${enc(businessId)}/periods`, { query: params }),

  period: (businessId: string, period: string) =>
    api.get<T.MetricsPeriod>(`/v1/metrics/${enc(businessId)}/periods/${enc(period)}`),

  savePeriod: (businessId: string, period: string, body: T.MetricsInput) =>
    api.put<{
      period: T.MetricsPeriod;
      valuation: T.Valuation;
      xpAwarded: number;
      badgesAwarded?: T.Badge[];
    }>(`/v1/metrics/${enc(businessId)}/periods/${enc(period)}`, body),

  valuation: (businessId: string, period?: string) =>
    api.get<T.Valuation>(`/v1/metrics/${enc(businessId)}/valuation`, { query: { period } }),

  targets: (businessId: string) =>
    api.get<{ items: T.Target[] }>(`/v1/metrics/${enc(businessId)}/targets`),

  createTarget: (businessId: string, body: T.TargetInput) =>
    api.post<T.Target>(`/v1/metrics/${enc(businessId)}/targets`, body),

  deleteTarget: (businessId: string, id: string) =>
    api.del<void>(`/v1/metrics/${enc(businessId)}/targets/${enc(id)}`),
};

/* --- gamification -------------------------------------------------------- */

export const gamificationApi = {
  /** Records the daily visit — this is what keeps a streak alive. */
  me: (businessId: string) => api.get<T.GamificationMe>(`/v1/gamification/${enc(businessId)}/me`),

  badges: (businessId: string) =>
    api.get<{ items: T.Badge[] }>(`/v1/gamification/${enc(businessId)}/badges`),

  evaluateBadges: (businessId: string) =>
    api.post<{ badgesAwarded: T.Badge[] }>(`/v1/gamification/${enc(businessId)}/badges/evaluate`),

  xpHistory: (businessId: string, params: { limit?: number; offset?: number } = {}) =>
    api.get<T.Paginated<T.XpAward>>(`/v1/gamification/${enc(businessId)}/xp-history`, { query: params }),

  leaderboard: (businessId: string) =>
    api.get<{ items: T.LeaderboardEntry[] }>(`/v1/gamification/${enc(businessId)}/leaderboard`),

  recomputeGrowthScore: (businessId: string) =>
    api.post<T.GamificationMe["growthScore"]>(
      `/v1/gamification/${enc(businessId)}/growth-score/recompute`,
    ),
};

/* --- todos --------------------------------------------------------------- */

export const todosApi = {
  list: (
    businessId: string,
    params: { status?: T.TodoStatus | "active"; category?: T.TodoCategory; limit?: number; offset?: number } = {},
  ) => api.get<T.TodosResponse>(`/v1/todos/${enc(businessId)}`, { query: params }),

  create: (businessId: string, body: T.TodoInput) =>
    api.post<{ todo: T.Todo; xpAwarded?: number; badgesAwarded?: T.Badge[] }>(
      `/v1/todos/${enc(businessId)}`,
      body,
    ),

  focus: (businessId: string) => api.get<{ focus: T.Todo | null }>(`/v1/todos/${enc(businessId)}/focus`),

  update: (businessId: string, todoId: string, body: Partial<T.TodoInput>) =>
    api.patch<{ todo: T.Todo; xpAwarded?: number; badgesAwarded?: T.Badge[] }>(
      `/v1/todos/${enc(businessId)}/${enc(todoId)}`,
      body,
    ),

  setFocus: (businessId: string, todoId: string) =>
    api.post<{ todo: T.Todo }>(`/v1/todos/${enc(businessId)}/${enc(todoId)}/focus`),

  remove: (businessId: string, todoId: string) =>
    api.del<void>(`/v1/todos/${enc(businessId)}/${enc(todoId)}`),
};

/* --- portfolio ----------------------------------------------------------- */

export interface ProductInput {
  name: string;
  description?: string | null;
  price?: number | null;
  cost?: number | null;
  revenueSharePercent?: number | null;
  isActive?: boolean;
}

export interface AvatarInput {
  name: string;
  description?: string | null;
  painPoints?: string[] | null;
  goals?: string[] | null;
  demographics?: string | null;
  channels?: string[] | null;
}

export const portfolioApi = {
  products: (businessId: string) =>
    api.get<T.ProductsResponse>(`/v1/portfolio/${enc(businessId)}/products`),
  createProduct: (businessId: string, body: ProductInput) =>
    api.post<T.Product>(`/v1/portfolio/${enc(businessId)}/products`, body),
  updateProduct: (businessId: string, id: string, body: Partial<ProductInput>) =>
    api.patch<T.Product>(`/v1/portfolio/${enc(businessId)}/products/${enc(id)}`, body),
  deleteProduct: (businessId: string, id: string) =>
    api.del<void>(`/v1/portfolio/${enc(businessId)}/products/${enc(id)}`),

  avatars: (businessId: string) =>
    api.get<T.Paginated<T.CustomerAvatar> | { items: T.CustomerAvatar[] }>(
      `/v1/portfolio/${enc(businessId)}/avatars`,
    ),
  createAvatar: (businessId: string, body: AvatarInput) =>
    api.post<T.CustomerAvatar>(`/v1/portfolio/${enc(businessId)}/avatars`, body),
  updateAvatar: (businessId: string, id: string, body: Partial<AvatarInput>) =>
    api.patch<T.CustomerAvatar>(`/v1/portfolio/${enc(businessId)}/avatars/${enc(id)}`, body),
  deleteAvatar: (businessId: string, id: string) =>
    api.del<void>(`/v1/portfolio/${enc(businessId)}/avatars/${enc(id)}`),
};

/* --- files --------------------------------------------------------------- */

export const filesApi = {
  list: (businessId: string, params: { limit?: number; offset?: number } = {}) =>
    api.get<T.Paginated<T.StoredFile> | { items: T.StoredFile[] }>(`/v1/files/${enc(businessId)}`, {
      query: params,
    }),

  upload: (businessId: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return api.post<T.StoredFile>(`/v1/files/${enc(businessId)}`, form);
  },

  get: (businessId: string, fileId: string) =>
    api.get<T.StoredFile>(`/v1/files/${enc(businessId)}/${enc(fileId)}`),

  remove: (businessId: string, fileId: string) =>
    api.del<void>(`/v1/files/${enc(businessId)}/${enc(fileId)}`),

  reindex: (businessId: string, fileId: string) =>
    api.post<T.StoredFile>(`/v1/files/${enc(businessId)}/${enc(fileId)}/reindex`),

  search: (businessId: string, body: { query: string; topK?: number }) =>
    api.post<{ items: T.FileSearchHit[] }>(`/v1/files/${enc(businessId)}/search`, body),

  /** Streams through the Worker after an access check — there is no public URL. */
  download: (businessId: string, fileId: string) =>
    apiFetch<Response>(`/v1/files/${enc(businessId)}/${enc(fileId)}/download`, { raw: true }),
};

/* --- RAI ----------------------------------------------------------------- */

export const raiApi = {
  conversations: (businessId: string) =>
    api.get<T.Paginated<T.RaiConversation> | { items: T.RaiConversation[] }>(
      `/v1/rai/${enc(businessId)}/conversations`,
    ),
  conversation: (businessId: string, id: string) =>
    api.get<T.RaiConversationDetail>(`/v1/rai/${enc(businessId)}/conversations/${enc(id)}`),
  deleteConversation: (businessId: string, id: string) =>
    api.del<void>(`/v1/rai/${enc(businessId)}/conversations/${enc(id)}`),
  send: (businessId: string, body: { message: string; conversationId?: string }) =>
    api.post<T.RaiSendResponse>(`/v1/rai/${enc(businessId)}/messages`, body),
  usage: (businessId: string) => api.get<T.RaiUsageResponse>(`/v1/rai/${enc(businessId)}/usage`),
};

/* --- education ----------------------------------------------------------- */

export const educationApi = {
  browse: (businessId: string) =>
    api.get<T.EducationBrowseResponse>(`/v1/education/${enc(businessId)}/browse`),

  search: (
    businessId: string,
    params: { q?: string; category?: string; level?: string; kind?: string; accessibleOnly?: boolean },
  ) => api.get<{ items: T.EducationItem[] }>(`/v1/education/${enc(businessId)}/search`, { query: params }),

  item: (businessId: string, idOrSlug: string) =>
    api.get<T.EducationItemDetail>(`/v1/education/${enc(businessId)}/items/${enc(idOrSlug)}`),

  /** Mint on press-play: the URL is signed and expires after four hours. */
  playback: (businessId: string, itemId: string) =>
    api.post<T.PlaybackResponse>(`/v1/education/${enc(businessId)}/items/${enc(itemId)}/playback`),

  progress: (businessId: string, itemId: string, secondsWatched: number) =>
    api.post<{
      progress: T.EducationProgress;
      xpAwarded?: number;
      badgesAwarded?: T.Badge[];
    }>(`/v1/education/${enc(businessId)}/items/${enc(itemId)}/progress`, { secondsWatched }),

  myProgress: (businessId: string) =>
    api.get<{ items: T.EducationItem[] }>(`/v1/education/${enc(businessId)}/progress`),
};

/* --- notifications ------------------------------------------------------- */

export const notificationsApi = {
  preferences: () => api.get<T.NotificationPreferences>("/v1/notifications/preferences"),
  updatePreferences: (body: Partial<T.NotificationPreferences>) =>
    api.patch<T.NotificationPreferences>("/v1/notifications/preferences", body),
  unregisterDevice: (pushToken: string) => api.del<void>("/v1/notifications/devices", { pushToken }),
};

/* --- admin --------------------------------------------------------------- */

export const adminApi = {
  overview: () => api.get<T.AdminOverview>("/v1/admin/overview"),

  businesses: (params: { q?: string; plan?: string; status?: string; limit?: number; offset?: number } = {}) =>
    api.get<T.Paginated<T.AdminBusinessRow>>("/v1/admin/businesses", { query: params }),

  business: (id: string) => api.get<T.AdminBusinessDetail>(`/v1/admin/businesses/${enc(id)}`),

  /** Super-admin lookup for granting staff access. Returns [] for q < 2 chars. */
  searchUsers: (q: string) =>
    api.get<{ items: T.AdminUserResult[] }>("/v1/admin/users", { query: { q } }),

  /** Irreversible. 409 with details.usePortal-style guard if a live Stripe sub exists. */
  deleteBusiness: (id: string) =>
    api.del<{ deleted: boolean; id: string; name: string }>(`/v1/admin/businesses/${enc(id)}`),

  assignNavigator: (id: string, navigatorUserId: string | null) =>
    api.post<T.AdminBusinessDetail>(`/v1/admin/businesses/${enc(id)}/navigator`, { navigatorUserId }),

  addNote: (id: string, body: string) =>
    api.post<T.AdminNote>(`/v1/admin/businesses/${enc(id)}/notes`, { body }),

  tasks: (params: { status?: string; businessId?: string; limit?: number; offset?: number } = {}) =>
    api.get<T.Paginated<T.AdminTask>>("/v1/admin/tasks", { query: params }),

  updateTask: (id: string, body: { status?: string; dueDate?: string | null; title?: string }) =>
    api.patch<T.AdminTask>(`/v1/admin/tasks/${enc(id)}`, body),

  invites: (params: { status?: string; limit?: number; offset?: number } = {}) =>
    api.get<T.Paginated<T.AdminInvite>>("/v1/admin/invites", { query: params }),

  /** The response carries the raw code exactly once — only its hash is stored. */
  createInvite: (body: { email?: string; plan: T.PlanId; expiresInDays?: number; note?: string }) =>
    api.post<T.AdminInvite>("/v1/admin/invites", body),

  revokeInvite: (id: string) => api.del<void>(`/v1/admin/invites/${enc(id)}`),

  scoringConfig: () => api.get<T.ScoringConfig>("/v1/admin/scoring-config"),
  updateScoringConfig: (body: Partial<T.ScoringConfig>) =>
    api.patch<T.ScoringConfig>("/v1/admin/scoring-config", body),

  updateSector: (id: string, body: { multiple?: number; basis?: string; name?: string }) =>
    api.patch<T.Sector>(`/v1/admin/sectors/${enc(id)}`, body),

  staff: () => api.get<{ items: T.AdminStaffMember[] }>("/v1/admin/staff"),
  // The role lives in the path: PATCH /v1/admin/staff/:userId
  updateStaff: ({ userId, staffRole }: { userId: string; staffRole: T.StaffRole | null }) =>
    api.patch<{ updated: boolean; staffRole: T.StaffRole | null }>(
      `/v1/admin/staff/${enc(userId)}`,
      { staffRole },
    ),

  cronRuns: (params: { job?: string; limit?: number } = {}) =>
    api.get<T.Paginated<T.CronRun> | { items: T.CronRun[] }>("/v1/admin/cron/runs", { query: params }),
  runCron: (job: string) => api.post<T.CronRun>(`/v1/admin/cron/${enc(job)}/run`),
};
